import { useBrowserLocation } from "@folio-2026/browser-location"
import { Section as SectionType } from "@folio-2026/experience"
import { useLoadingStore } from "@folio-2026/section-loader"
import {
  AppFrame,
  AppHeightChecker,
  ErrorBoundary,
  EVENTS,
  Menu,
} from "@folio-2026/shared"
import { SplitText } from "gsap/SplitText"
import { gsap } from "gsap/all"
import { ReactElement, useEffect, useRef, useState } from "react"
import { Outlet, useLocation } from "react-router"

import useExperience from "hooks/useExperience"

import Providers from "./Providers"
import SectionGuestbook from "./Sections/Guestbook"
import SectionLoading from "./Sections/Loading"
import Scroller from "./Sections/Scroller"
import Section from "./Sections/Section"
import SectionContact from "./Sections/SectionContact"
import SectionWorks from "./Sections/Works"
import ProjectContent from "./Sections/Works/ProjectContent"

gsap.registerPlugin(SplitText)

export type SectionDOMElement = {
  name: SectionType
  height?: "full" | "random"
  component: () => ReactElement
}

const sections: SectionDOMElement[] = [
  {
    name: "loader",
    component: SectionLoading,
  },
  {
    name: "works",
    component: SectionWorks,
  },
  {
    name: "guestbook",
    component: SectionGuestbook,
  },
  {
    name: "contact",
    height: "full",
    component: SectionContact,
  },
]

export default function App() {
  const { setBrowserLocation } = useBrowserLocation()
  const { hasClicked } = useLoadingStore()

  const isInitialLanding = useRef(true)

  const location = useLocation()
  const isMainPage = location.pathname === "/"

  useEffect(() => {
    setBrowserLocation(location)
  }, [location, setBrowserLocation])

  const experience = useExperience()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    if (!experience) return
    experience.on(EVENTS.Menu.open, () => setIsMenuOpen(true))
    experience.on(EVENTS.Menu.close, () => setIsMenuOpen(false))
  }, [experience])

  useEffect(() => {
    if (!experience) return

    const textEl = document.getElementById("tcmg-loader-text")
    if (!textEl) return

    const tl = gsap.timeline()
    revealLoaderCaption(tl)
    tl.call(() => {
      revealLoaderText({
        onClick: (event: MouseEvent) => {
          event.preventDefault()
          event.stopPropagation()
          experience.trigger(EVENTS.Loader.EnterCTA.Click)
        },
      })
      textEl.style.opacity = "1"
    })
  }, [experience])

  useEffect(() => {
    if (!isMainPage) {
      isInitialLanding.current = false
    }

    if (
      !experience?.lenis ||
      hasClicked ||
      !isMainPage ||
      !isInitialLanding.current
    ) {
      return
    }

    experience.lenis.stop()
  }, [experience, hasClicked, isMainPage])

  return (
    <Providers>
      <AppHeightChecker />

      <AppFrame />
      <Menu
        isOpen={isMenuOpen}
        onCloseClick={() => experience?.trigger(EVENTS.Menu.click)}
        onItemClick={(el) => experience?.trigger(EVENTS.Menu.itemClick, [el])}
      />

      <ErrorBoundary>
        <ProjectContent />
        <Outlet />
      </ErrorBoundary>

      <Scroller sections={sections}>
        {sections.map(({ name, component: Component, ...rest }) => (
          <Section key={name} name={name} {...rest}>
            <Component />
          </Section>
        ))}
      </Scroller>
    </Providers>
  )
}

function revealLoaderCaption(timeline: gsap.core.Timeline) {
  const el = document.getElementById("tcmg-loader-caption")
  if (!el) return

  const lines = Array.from(el.children).filter(
    (child) => child.tagName === "P",
  ) as HTMLElement[]

  if (lines.length === 0) return

  gsap.set(el, { opacity: 1 })
  gsap.set(lines, { opacity: 0 })
  timeline.to(lines, {
    opacity: 1,
    duration: 0.5,
    stagger: 0.2,
    ease: "power2.out",
  })
}

function revealLoaderText({
  onClick,
}: {
  onClick: (event: MouseEvent) => void
}) {
  const el = document.getElementById("tcmg-loader-text")

  if (!el) {
    console.warn("Loader text element not found")
    return
  }

  // Get only <p> elements (filter out button)
  const paragraphs = Array.from(el.children).filter(
    (child) => child.tagName === "P",
  ) as HTMLParagraphElement[]

  const button = el.querySelector("#enter-cta") as HTMLButtonElement
  if (!button) {
    console.warn("Enter CTA button not found")
    return
  }

  if (paragraphs.length === 0) {
    return
  }

  button.addEventListener("click", onClick)

  // Helper function for scrambling words (similar to Text.textScramble)
  function scrambleWord(text: string, progress: number): string {
    if (progress >= 1) {
      return text
    }

    const chars = "jompaWB)#@$"
    const charsLength = chars.length
    const textLength = text.length
    const revealedChars = Math.floor(textLength * progress)

    // Build the scrambled word: revealed chars + scrambled chars
    let scrambled = ""
    for (let i = 0; i < textLength; i++) {
      if (i < revealedChars) {
        // Show actual character
        scrambled += text[i]
      } else {
        // Show random scrambled character
        scrambled += chars[Math.floor(Math.random() * charsLength)]
      }
    }

    return scrambled
  }

  // Split each paragraph into words
  const splitInstances = paragraphs.map((p) => {
    return new SplitText(p, { type: "lines,words" })
  })

  // Create timeline
  const tl = gsap.timeline()

  // Track current timeline position for line delays
  let currentTimelinePosition = 0

  // Animate each paragraph's words (each paragraph is a "line")
  splitInstances.forEach((split, paragraphIndex) => {
    if (paragraphIndex > 0) {
      currentTimelinePosition += 1
    }

    // Iterate through words in this paragraph/line
    split.words.forEach((word, wordIndex) => {
      const originalText = word.textContent || ""
      const proxy = { progress: 0 }

      // Store original text in word element for reference
      word.setAttribute("data-original-text", originalText)

      // Initially set word to scrambled state and invisible
      word.textContent = scrambleWord(originalText, 0)
      gsap.set(word, { opacity: 0 })

      // Calculate start time: current timeline position + word stagger
      const startTime = currentTimelinePosition + wordIndex * 0.2

      // Fade in to 0.5 opacity when scrambling starts
      tl.to(
        word,
        {
          opacity: 0.5,
          duration: 0.1,
          ease: "none",
        },
        startTime,
      )

      let updateCounter = 0
      let lastScrambledText = ""

      tl.to(
        proxy,
        {
          progress: 1,
          duration: 0.5,
          ease: "none",
          onUpdate: () => {
            updateCounter++
            // Only call scrambleWord every 5 onUpdate calls (or on first call)
            if (updateCounter % 5 === 0 || lastScrambledText === "") {
              lastScrambledText = scrambleWord(originalText, proxy.progress)
            }
            word.textContent = lastScrambledText

            // Interpolate opacity from 0.5 to 1 as progress increases
            const opacity = 0.5 + proxy.progress * 0.5
            gsap.set(word, { opacity })
          },
          onComplete: () => {
            word.textContent = originalText
            gsap.set(word, { opacity: 1 })
          },
        },
        startTime + 0.1,
      )
    })

    // Update timeline position: last word start time + fade duration + reveal duration
    const lastWordIndex = split.words.length - 1
    const lastWordStartTime = currentTimelinePosition + lastWordIndex * 0.1
    currentTimelinePosition = lastWordStartTime + 0.2 + 0.5 // fade duration + reveal duration
  })
}
